import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CHANNEL_NAMES,
  errorMessage,
  mintLinkCode,
  type LinkCode,
  type MessagingChannel,
} from "./api";
import { ARIA_DISABLED_LOOK, TAP_ON_PHONE } from "./classes";
import { Spinner } from "./Spinner";

// A fresh code every five minutes while the block is on screen; the server keeps the previous one
// valid for a short grace, so a code scanned just before a rotation still works.
const ROTATE_MS = 5 * 60 * 1000;

/** QR with the code under it, an "or" divider, the deeplink button, then the one warning. */
export function LinkCodeBlock({ channel }: { channel: MessagingChannel }) {
  const [link, setLink] = useState<LinkCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const start = useRef<HTMLAnchorElement>(null);
  const tryAgain = useRef<HTMLButtonElement>(null);
  const retryRunning = useRef(false);
  const mintedAt = useRef(0);
  const mintedFor = useRef<MessagingChannel | null>(null);
  // Mints can overlap (the timer and a tab coming back): only the newest request's answer shows.
  const latest = useRef(0);
  const name = CHANNEL_NAMES[channel];

  const mint = useCallback(async () => {
    const request = ++latest.current;
    mintedAt.current = Date.now();
    mintedFor.current = channel;
    try {
      const fresh = await mintLinkCode(channel);
      if (request !== latest.current) return false;
      setLink(fresh);
      setError(null);
      return true;
    } catch (failure) {
      if (request === latest.current) setError(errorMessage(failure));
      return false;
    }
  }, [channel]);

  // Try again goes once the code arrives: focus moves on to the main control, unless the user
  // has already moved it somewhere else.
  const retry = async () => {
    if (retryRunning.current) return;
    retryRunning.current = true;
    setRetrying(true);
    try {
      const minted = await mint();
      const focus = document.activeElement;
      if (minted && (focus === tryAgain.current || focus === document.body || !focus)) {
        start.current?.focus();
      }
    } finally {
      retryRunning.current = false;
      setRetrying(false);
    }
  };

  useEffect(() => {
    // A remount of the same block (StrictMode in dev) keeps the code it is already fetching.
    if (mintedFor.current !== channel || Date.now() - mintedAt.current >= ROTATE_MS) void mint();
    const timer = window.setInterval(() => void mint(), ROTATE_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - mintedAt.current >= ROTATE_MS) {
        void mint();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [mint, channel]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex h-44 w-44 items-center justify-center rounded-xl border bg-white p-3">
        {link ? (
          <QRCodeSVG value={link.url} size={152} level="M" aria-label={`${name} link code`} />
        ) : (
          // A failed mint leaves the tile empty (same size) until Try again: the error line says why.
          (!error || retrying) && <Spinner label="Loading code" />
        )}
      </div>
      <div
        className="font-mono text-sm tracking-wider text-muted-foreground"
        data-testid="link-code"
      >
        {link?.code ?? " "}
      </div>
      <div className="flex w-full items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>
      <Button asChild className={cn("w-full", TAP_ON_PHONE, ARIA_DISABLED_LOOK)}>
        {/* Focusable before the code arrives (so a dialog can open on it); does nothing until then. */}
        <a
          ref={start}
          href={link?.url}
          role="link"
          tabIndex={0}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!link}
          data-autofocus
          onClick={(event) => {
            if (!link) event.preventDefault();
          }}
        >
          Get started with {name}
        </a>
      </Button>
      {error && (
        <div className="flex items-center gap-2">
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
          <Button
            ref={tryAgain}
            variant="ghost"
            size="sm"
            className={cn(TAP_ON_PHONE, ARIA_DISABLED_LOOK)}
            aria-disabled={retrying}
            onClick={() => void retry()}
          >
            Try again
          </Button>
        </div>
      )}
      {/* The one warning, as a warning box (muted until the warning token lands with F). */}
      <p className="flex w-full items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        Whoever sends this code from their {name} gets access to your account.
      </p>
    </div>
  );
}
